import React, { useState, useEffect } from 'react'
import { Card, Table, Button, Tag, Modal, Rate, Space, message, Image, Select, Input } from 'antd'
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { Search } = Input
const { Option } = Select

const RateManage = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentItem, setCurrentItem] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async (page = 1, status = filterStatus, search = searchText) => {
    setLoading(true)
    try {
      let url = `${API_BASE}/api/rate/admin/list?page=${page}&limit=20`
      if (status !== 'all') url += `&status=${status}`
      
      const res = await fetch(url)
      const result = await res.json()
      
      if (result.success) {
        let items = result.data || []
        if (search) {
          items = items.filter(item => 
            item.product_name?.includes(search) || 
            item.content?.includes(search)
          )
        }
        setData(items)
        setPagination(prev => ({ ...prev, total: result.total || 0, current: page }))
      }
    } catch (err) {
      message.error('加载失败')
    }
    setLoading(false)
  }

  const handleReview = async (id, status, rejectReason = '') => {
    try {
      const res = await fetch(`${API_BASE}/rate/${id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, rejectReason })
      })
      const result = await res.json()
      
      if (result.success) {
        message.success(status === 'approved' ? '审核通过' : '已驳回')
        setDetailVisible(false)
        loadData()
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const showDetail = (item) => {
    setCurrentItem(item)
    setDetailVisible(true)
  }

  const columns = [
    { title: '评价ID', dataIndex: 'id', width: 180,
      render: id => <span style={{ fontSize: 12, color: '#999' }}>{id.substring(0, 20)}...</span>
    },
    { title: '商品', dataIndex: 'product_name', width: 150 },
    { title: '评分', dataIndex: 'rating', width: 120,
      render: rating => <Rate disabled defaultValue={rating} style={{ fontSize: 14 }} />
    },
    { title: '评价内容', dataIndex: 'content', width: 200,
      render: text => text?.substring(0, 30) + (text?.length > 30 ? '...' : '')
    },
    { title: '标签', dataIndex: 'tags', width: 200,
      render: tags => (
        <Space wrap>
          {(tags || []).slice(0, 3).map((tag, i) => (
            <Tag key={i} color="blue">{tag}</Tag>
          ))}
        </Space>
      )
    },
    { title: '图片', dataIndex: 'images', width: 100,
      render: images => images?.length > 0 ? (
        <Tag color="green">{images.length}张</Tag>
      ) : '-'
    },
    { title: '状态', dataIndex: 'status', width: 100,
      render: s => {
        const map = {
          pending: { color: 'orange', text: '待审核' },
          approved: { color: 'green', text: '已通过' },
          rejected: { color: 'red', text: '已驳回' }
        }
        const { color, text } = map[s] || map.pending
        return <Tag color={color}>{text}</Tag>
      }
    },
    { title: '时间', dataIndex: 'created_at', width: 160,
      render: t => t?.substring(0, 16)
    },
    { title: '操作', width: 100, fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>查看</Button>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>⭐ 评价管理</h2>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <Search
            placeholder="搜索商品/内容"
            style={{ width: 300 }}
            onSearch={val => { setSearchText(val); loadData(1, filterStatus, val) }}
            allowClear
          />
          <Select value={filterStatus} onChange={val => { setFilterStatus(val); loadData(1, val, searchText) }} style={{ width: 120 }}>
            <Option value="all">全部</Option>
            <Option value="pending">待审核</Option>
            <Option value="approved">已通过</Option>
            <Option value="rejected">已驳回</Option>
          </Select>
          <Button onClick={() => loadData()}>刷新</Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: loadData
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title="评价详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          currentItem?.status === 'pending' ? (
            <Space>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => handleReview(currentItem.id, 'approved')}>
                通过
              </Button>
              <Button danger icon={<CloseOutlined />} onClick={() => handleReview(currentItem.id, 'rejected')}>
                驳回
              </Button>
            </Space>
          ) : null
        }
        width={700}
      >
        {currentItem && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <b>商品：</b>{currentItem.product_name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <b>评分：</b>
              <Rate disabled value={currentItem.rating} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <b>标签：</b>
              {(currentItem.tags || []).map((tag, i) => (
                <Tag key={i} color="blue">{tag}</Tag>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <b>评价内容：</b>
              <p style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>{currentItem.content}</p>
            </div>
            {currentItem.images?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <b>图片：</b>
                <Space>
                  {currentItem.images.map((img, i) => (
                    <Image key={i} src={img} width={80} height={80} style={{ objectFit: 'cover' }} />
                  ))}
                </Space>
              </div>
            )}
            <div style={{ color: '#999' }}>
              评价时间：{currentItem.created_at}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default RateManage
